import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query';
import { backendURL } from 'src/constant/api/backendURL';
import { IRootState } from '../store';
import {
    addNewProductAPI,
    deleteProductAPI,
    getAllProductsAPI,
    getVendorProductsAPI,
    getLatestProductsAPI,
    updateProductAPI,
    updateProductImageAPI,
} from 'src/constant/api/productsAPI';
import axios, { AxiosResponse } from 'axios';
import Cookies from 'js-cookie';

export interface IProduct {
    id: string;
    name: string;
    price: number;
    category: string;
    sub_category?: string;
    image_dir: any;
    description: string;
    specification?: string;
    vendor_name?: string;
    vendor_address?: string;
    discount?: number;
    status: string;
    rejectNote?: string | null;
    quantity: number;
    createdAt: string;
    updatedAt: string;
}

// backend update new UpdatedAt
export interface IUpdateProduct {
    id: string;
    name: string;
    price: number;
    category: string;
    sub_category?: string;
    description: string;
    specification?: string;
    vendor_name?: string;
    vendor_address?: string;
    discount?: number;
    status: string;
    rejectNote?: string | null;
    quantity: number;
    updatedAt?: string;
    files?: File[];
}

export interface IAddNewProduct {
    name: string;
    category: string;
    sub_category?: string;
    price: number;
    quantity: number;
    description: string;
    specification?: string;
    vendor_name?: string;
    vendor_address?: string;
    files?: File[];
}

export interface IProductImage {
    image_dir: any;
    id: string;
}

export interface IProductInStock {
    items: IProduct[];
    vendorItems: IProduct[];
    latestItems: IProduct[];
    error: Object | null;
    status: string;
}

export const fetchProducts = createAsyncThunk<IProduct[], void>(
    'products/getAllProducts',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await getAllProductsAPI();

            const formattedTime = (dateObject: Date) => {
                const hours = dateObject.getHours().toString().padStart(2, '0');
                const minutes = dateObject.getMinutes().toString().padStart(2, '0');
                const day = dateObject.getDate().toString().padStart(2, '0');
                const month = (dateObject.getMonth() + 1).toString().padStart(2, '0');
                const year = dateObject.getFullYear();
                return `${hours}:${minutes} - ${day}/${month}/${year}`;
            };

            // image_dir now contains a Cloudinary URL directly from the API
            const products: IProduct[] = data.map((product: any) => ({
                id: product.id,
                name: product.name,
                price: product.price,
                quantity: product.quantity,
                category: product.category,
                sub_category: product.sub_category || '',
                image_dir: Array.isArray(product.image_dir) ? product.image_dir : (product.image_dir ? [product.image_dir] : []),
                description: product.description,
                specification: product.specification || '',
                vendor_name: product.vendor_name || '',
                vendor_address: product.vendor_address || '',
                discount: undefined,
                status: product.status,
                rejectNote: product.rejectNote || null,
                createdAt: formattedTime(new Date(product.createdAt)),
                updatedAt: formattedTime(new Date(product.updatedAt)),
            }));

            return products;
        } catch (error: any) {
            if (error.response && error.response.message) return rejectWithValue(error.response.message);
            else return rejectWithValue(error.message);
        }
    },
);

export const updateProductImage = createAsyncThunk(
    'products/updateProductImage',
    async ({ image_dir, id }: IProductImage, { rejectWithValue }) => {
        try {
            const token = Cookies.get('accessToken');
            const response = await updateProductImageAPI(token, image_dir, id);

            // const productImage: any = await getProductImageAPI(id);
            // const productImageUrl = URL.createObjectURL(productImage.data);
            // const imageData: IProductImage = { image_dir: productImageUrl, id: id };
            // console.log(imageData);
            // return imageData;
            return;
        } catch (err: any) {
            return rejectWithValue(err.message);
        }
    },
);

export const updateProduct = createAsyncThunk(
    'products/productUpdate',
    async (product: IUpdateProduct, { rejectWithValue }) => {
        try {
            const adminToken = Cookies.get('accessToken');
            const { data } = await updateProductAPI(product, adminToken);

            return {
                id: data.id,
                name: data.name,
                price: data.price,
                quantity: data.quantity,
                category: data.category,
                description: data.description,
                discount: undefined,
                status: data.status,
            };
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const addNewProduct = createAsyncThunk(
    'products/addNewProduct',
    async (productData: IAddNewProduct, { rejectWithValue }) => {
        try {
            const adminToken = Cookies.get('accessToken');
            const { data } = await addNewProductAPI(productData, adminToken);

            return data;
        } catch (err: any) {
            return rejectWithValue(err);
        }
    },
);

export const deleteProduct = createAsyncThunk(
    'products/deleteProduct',
    async (productId: string, { rejectWithValue }) => {
        try {
            const adminToken = Cookies.get('accessToken');
            const { data } = await deleteProductAPI(productId, adminToken);
            return productId; // Return the deleted product ID
        } catch (err: any) {
            return rejectWithValue(err);
        }
    },
);

export const fetchVendorProducts = createAsyncThunk<IProduct[], void>(
    'products/getVendorProducts',
    async (_, { rejectWithValue }) => {
        try {
            const token = Cookies.get('accessToken');
            const { data } = await getVendorProductsAPI(token);

            const formattedTime = (dateObject: Date) => {
                const hours = dateObject.getHours().toString().padStart(2, '0');
                const minutes = dateObject.getMinutes().toString().padStart(2, '0');
                const day = dateObject.getDate().toString().padStart(2, '0');
                const month = (dateObject.getMonth() + 1).toString().padStart(2, '0');
                const year = dateObject.getFullYear();
                return `${hours}:${minutes} - ${day}/${month}/${year}`;
            };

            const products: IProduct[] = data.map((product: any) => ({
                id: product.id,
                name: product.name,
                price: product.price,
                quantity: product.quantity,
                category: product.category,
                sub_category: product.sub_category || '',
                image_dir: Array.isArray(product.image_dir) ? product.image_dir : (product.image_dir ? [product.image_dir] : []),
                description: product.description,
                specification: product.specification || '',
                vendor_name: product.vendor_name || '',
                vendor_address: product.vendor_address || '',
                discount: undefined,
                status: product.status,
                rejectNote: product.rejectNote || null,
                createdAt: formattedTime(new Date(product.createdAt)),
                updatedAt: formattedTime(new Date(product.updatedAt)),
            }));

            return products;
        } catch (error: any) {
            if (error.response && error.response.message) return rejectWithValue(error.response.message);
            else return rejectWithValue(error.message);
        }
    },
);

export const fetchLatestProducts = createAsyncThunk<IProduct[], number>(
    'products/getLatestProducts',
    async (limit = 4, { rejectWithValue }) => {
        try {
            const { data } = await getLatestProductsAPI(limit);
            return data.map((product: any) => ({
                id: product.id,
                name: product.name,
                price: product.price,
                quantity: product.quantity,
                category: product.category,
                sub_category: product.sub_category || '',
                image_dir: Array.isArray(product.image_dir) ? product.image_dir : (product.image_dir ? [product.image_dir] : []),
                description: product.description,
                status: product.status,
                rejectNote: product.rejectNote || null,
                createdAt: product.createdAt,
                updatedAt: product.updatedAt,
            }));
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    },
);

const initialState: IProductInStock = {
    items: [],
    vendorItems: [],
    latestItems: [],
    error: null,
    status: 'idle',
};

export const productsSlice = createSlice({
    name: 'products',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchProducts.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchProducts.fulfilled, (state, { payload }) => {
                state.status = 'succeed';
                state.items = payload;
            })
            .addCase(fetchProducts.rejected, (state, { payload }) => {
                state.status = 'failed';
                state.error = payload ?? null;
            })
            .addCase(updateProductImage.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(updateProductImage.fulfilled, (state, { payload }) => {
                state.status = 'succeed';
                // const temp = [...state.items];
                // temp.forEach((item) => {
                //     if (item.id === payload.id) item.image_dir = payload.image_dir;
                // });
                // state.items = temp;
            })
            .addCase(updateProductImage.rejected, (state, { payload }) => {
                state.status = 'failed';
                state.error = payload ?? null;
            })
            .addCase(updateProduct.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(updateProduct.fulfilled, (state, { payload }) => {
                state.status = 'succeed';

                // -- first way --
                const index = state.items.findIndex((item) => item.id === payload.id);
                if (index !== -1) {
                    state.items[index] = {
                        ...state.items[index],
                        ...payload,
                    };
                }

                // -- Second way --
                // const temp = [...state.items];
                // temp.forEach((item) => {
                //     if (item.id === payload.id) {
                //         item = {
                //             ...item,
                //             name: payload.name,
                //             price: payload.price,
                //             description: payload.description,
                //             status: payload.status,
                //             quantity: payload.quantity,
                //             category: payload.category,
                //         };
                //     }
                // });
                // state.items = temp;
            })
            .addCase(updateProduct.rejected, (state, { payload }) => {
                state.status = 'failed';
                state.error = payload ?? null;
            })
            .addCase(addNewProduct.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(addNewProduct.fulfilled, (state, { payload }) => {
                state.status = 'succeed';
                // Product will be added via fetchProducts, just mark as success
            })
            .addCase(addNewProduct.rejected, (state, { payload }) => {
                state.status = 'failed';
                state.error = payload ?? null;
            })
            .addCase(deleteProduct.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(deleteProduct.fulfilled, (state, { payload }) => {
                state.status = 'succeed';
                // Remove the deleted product from the items array
                state.items = state.items.filter(item => item.id !== payload);
            })
            .addCase(deleteProduct.rejected, (state, { payload }) => {
                state.status = 'failed';
                state.error = payload ?? null;
            })
            .addCase(fetchVendorProducts.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchVendorProducts.fulfilled, (state, { payload }) => {
                state.status = 'succeed';
                state.vendorItems = payload;
            })
            .addCase(fetchVendorProducts.rejected, (state, { payload }) => {
                state.status = 'failed';
                state.error = payload ?? null;
            })
            .addCase(fetchLatestProducts.pending, (state) => {
                // Don't set global loading for latest products
            })
            .addCase(fetchLatestProducts.fulfilled, (state, { payload }) => {
                state.latestItems = payload;
            })
            .addCase(fetchLatestProducts.rejected, (state, { payload }) => {
                state.error = payload ?? null;
            });
    },
});
